export type NavigationKind = 'push' | 'replace' | 'pop';

/**
 * Meldet SPA-Navigationen: wrappt `history.pushState`/`replaceState` und hört auf `popstate`.
 * Gibt eine Funktion zurück, die den ursprünglichen Zustand wiederherstellt (sofern niemand
 * anderes inzwischen dieselben Methoden überschrieben hat).
 */
export function watchNavigation(onNavigate: (kind: NavigationKind) => void): () => void {
  const history = window.history;
  const origPush = history.pushState;
  const origReplace = history.replaceState;

  const notify = (kind: NavigationKind) => {
    try {
      onNavigate(kind);
    } catch {
      // Fehler im SDK dürfen die Navigation der Host-App nie stören.
    }
  };

  const push: History['pushState'] = function (this: History, ...args) {
    const r = origPush.apply(this, args);
    notify('push');
    return r;
  };
  const replace: History['replaceState'] = function (this: History, ...args) {
    const r = origReplace.apply(this, args);
    notify('replace');
    return r;
  };

  history.pushState = push;
  history.replaceState = replace;
  const onPop = () => notify('pop');
  window.addEventListener('popstate', onPop);

  return () => {
    if (history.pushState === push) history.pushState = origPush;
    if (history.replaceState === replace) history.replaceState = origReplace;
    window.removeEventListener('popstate', onPop);
  };
}
